import axios, { AxiosError, AxiosRequestConfig } from 'axios';

export const TOKEN_KEY = 'bcl_token';
export const REFRESH_TOKEN_KEY = 'bcl_refresh_token';
const REFRESH_LOCK_KEY = 'bcl_refresh_lock';
const REFRESH_LOCK_TTL_MS = 10_000;
const REFRESH_LOCK_WAIT_MS = 5_000;

export const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080';

/** Resolve a server-relative URL (e.g. /uploads/...) against the API base URL. */
export function resolveAssetUrl(url: string | undefined | null): string | null {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_BASE_URL}${url}`;
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Decode the `exp` (seconds since epoch) claim from a JWT, or null if it can't be parsed. */
export function readTokenExpiry(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    ) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** True if the token is missing or has already expired. */
export function isTokenExpired(token: string | null): boolean {
  if (!token) return true;
  const exp = readTokenExpiry(token);
  if (exp == null) return false;
  return Date.now() / 1000 >= exp;
}

function forceLogout() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  const path = window.location.pathname;
  if (!path.startsWith('/login') && !path.startsWith('/signup') && path !== '/' && !path.startsWith('/share/')) {
    window.location.href = '/login';
  }
}

// ── Refresh-token rotation ────────────────────────────────────────────
// Single in-flight refresh promise — concurrent expired requests await the
// same network round-trip rather than spawning N refresh calls.
let refreshInFlight: Promise<string | null> | null = null;

type RefreshLock = {
  owner: string;
  expiresAt: number;
};

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function refreshLockOwner() {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `${Date.now()}-${random}`;
}

function readRefreshLock(): RefreshLock | null {
  try {
    const raw = localStorage.getItem(REFRESH_LOCK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RefreshLock>;
    if (typeof parsed.owner !== 'string' || typeof parsed.expiresAt !== 'number') {
      return null;
    }
    return { owner: parsed.owner, expiresAt: parsed.expiresAt };
  } catch {
    return null;
  }
}

function releaseRefreshLock(owner: string) {
  const lock = readRefreshLock();
  if (lock?.owner === owner) {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  }
}

async function refreshAccessToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null;
  const initialRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!initialRefresh) return null;

  // Refresh tokens are one-use/rotating. localStorage is shared by tabs, but
  // JS memory is not, so two tabs can otherwise refresh the same token at once:
  // one succeeds, the other gets "Refresh token not found" and logs out.
  const owner = refreshLockOwner();
  const deadline = Date.now() + REFRESH_LOCK_WAIT_MS;
  while (true) {
    const currentRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!currentRefresh) return null;
    if (currentRefresh !== initialRefresh) {
      return localStorage.getItem(TOKEN_KEY);
    }

    const lock = readRefreshLock();
    const now = Date.now();
    if (!lock || lock.expiresAt <= now) {
      localStorage.setItem(
        REFRESH_LOCK_KEY,
        JSON.stringify({ owner, expiresAt: now + REFRESH_LOCK_TTL_MS })
      );
      if (readRefreshLock()?.owner === owner) break;
    }

    if (now >= deadline) {
      return localStorage.getItem(TOKEN_KEY);
    }
    await sleep(120);
  }

  const refresh = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refresh) {
    releaseRefreshLock(owner);
    return null;
  }

  // Bare axios instance — bypass our interceptors so we don't recurse.
  try {
    const { data } = await axios.post<{
      accessToken: string;
      refreshToken: string;
    }>(`${API_BASE_URL}/api/auth/refresh`, { refreshToken: refresh });
    if (localStorage.getItem(REFRESH_TOKEN_KEY) !== refresh) {
      return localStorage.getItem(TOKEN_KEY);
    }
    localStorage.setItem(TOKEN_KEY, data.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    return data.accessToken;
  } catch {
    // If another tab finished rotation while this request was failing, keep the
    // newer token pair. This avoids a stale 404 clearing a valid session.
    await sleep(250);
    if (localStorage.getItem(REFRESH_TOKEN_KEY) !== refresh) {
      return localStorage.getItem(TOKEN_KEY);
    }
    if (localStorage.getItem(REFRESH_TOKEN_KEY) === refresh) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
    return null;
  } finally {
    releaseRefreshLock(owner);
  }
}

async function ensureFreshToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = refreshAccessToken().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/**
 * Auth endpoints must never go through token attach/refresh: logging in with a
 * stale-but-expired token still in storage would otherwise fire /refresh first
 * (and a 401 from /login means wrong credentials, not an expired session).
 */
function isAuthPath(url?: string): boolean {
  if (!url) return false;
  return (
    url.includes('/api/auth/login') ||
    url.includes('/api/auth/signup') ||
    url.includes('/api/auth/refresh') ||
    url.includes('/api/auth/logout') ||
    url.includes('/api/auth/forgot-password') ||
    url.includes('/api/auth/reset-password') ||
    url.includes('/api/auth/verify-email')
  );
}

// Inject JWT — proactively refresh if access is expired.
api.interceptors.request.use(async (config) => {
  if (typeof window === 'undefined') return config;
  if (isAuthPath(config.url)) return config;

  let token = localStorage.getItem(TOKEN_KEY);
  if (token && isTokenExpired(token)) {
    token = await ensureFreshToken();
    if (!token) {
      forceLogout();
      return Promise.reject(new axios.Cancel('Token expired'));
    }
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    (config as AxiosRequestConfig & { _authToken?: string })._authToken = token;
  }
  return config;
});

// On 401/403 — try refreshing once before giving up.
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config as (AxiosRequestConfig & { _retried?: boolean; _authToken?: string }) | undefined;
    if ((status === 401 || status === 403) && original && !original._retried && !isAuthPath(original.url) && typeof window !== 'undefined') {
      const currentToken = localStorage.getItem(TOKEN_KEY);
      if (original._authToken && currentToken && original._authToken !== currentToken) {
        original._retried = true;
        const replacement = isTokenExpired(currentToken) ? await ensureFreshToken() : currentToken;
        if (replacement) {
          original.headers = { ...(original.headers || {}), Authorization: `Bearer ${replacement}` };
          return api.request(original);
        }
        forceLogout();
        return Promise.reject(error);
      }
      original._retried = true;
      const fresh = await ensureFreshToken();
      if (fresh) {
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${fresh}` };
        return api.request(original);
      }
      forceLogout();
    }
    return Promise.reject(error);
  }
);

/** Persist the access + refresh token pair returned by login / signup / refresh. */
export function setAuthTokens(tokens: { accessToken: string; refreshToken?: string | null } | null) {
  if (typeof window === 'undefined') return;
  refreshInFlight = null;
  if (!tokens) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    return;
  }
  localStorage.setItem(TOKEN_KEY, tokens.accessToken);
  if (tokens.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
}

/** @deprecated kept for compatibility — prefer setAuthTokens. */
export function setAuthToken(token: string | null) {
  if (typeof window === 'undefined') return;
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
}

export function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Turn an axios/API error into a human-readable message. Surfaces field-level
 * validation details (e.g. "Date of birth: must be a past date") when the
 * backend returns them, instead of the generic "Validation failed".
 */
export function apiErrorMessage(e: unknown, fallback = 'Something went wrong'): string {
  const err = e as { response?: { data?: { message?: string; details?: Record<string, string> } } };
  const data = err?.response?.data;
  if (data?.details && Object.keys(data.details).length > 0) {
    const labels: Record<string, string> = {
      dateOfBirth: 'Date of birth',
      yearsInService: 'Years in service',
      serviceType: 'Service type',
      salonName: 'Salon name',
      email: 'Email',
      password: 'Password',
      firstName: 'First name',
      lastName: 'Last name',
    };
    return Object.entries(data.details)
      .map(([field, msg]) => `${labels[field] ?? field}: ${msg}`)
      .join('. ');
  }
  return data?.message || fallback;
}

/** Best-effort server-side revoke + clear local storage. */
export async function logoutFully() {
  const refresh = getRefreshToken();
  try {
    await axios.post(`${API_BASE_URL}/api/auth/logout`, { refreshToken: refresh });
  } catch {
    // ignore — we still clear local state
  }
  if (typeof window === 'undefined') return;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}
